import { Capacitor } from '@capacitor/core'
import { IonModal } from '@ionic/react'
import { GlobalStyles, Stack } from '@mui/material'
import { FC, RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { isIOS } from 'react-device-detect'
import { Fade } from 'utoniq-ui'

import { ResultPlusScript } from '@/components/layouts/ResultPlus'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { useMessage, useRouter } from '@/core'

import {
  BuyNowCompletion,
  BuyNowDeliveryInfo,
  BuyNowDeliveryInfoProps,
  BuyNowItemDetail,
  BuyNowItemDetailProps,
  BuyNowPaymentCreate,
  BuyNowPaymentCreateProps,
  BuyNowSMSVerification,
  BuyNowSMSVerificationProps,
  BuyNowUserInfo,
  BuyNowUserInfoProps,
  StepKey,
} from './components'

export type BuyNowModalProps = Omit<
  BuyNowItemDetailProps &
    BuyNowDeliveryInfoProps &
    BuyNowPaymentCreateProps &
    BuyNowUserInfoProps &
    BuyNowSMSVerificationProps & {
      open: boolean
      onClose: () => void
      onPurchaseItem: () => Promise<void>
    },
  'onNextStep' | 'onConfirmOrder' | 'onClickClose'
>

export const BuyNowModal: FC<BuyNowModalProps> = ({
  open,
  onClose,
  onPurchaseItem,
  onCreatePayment,
  onUserInfoSetting,
  onSendVerificationSMS,
  onVerificationPhoneNumber,
  ...props
}) => {
  const isNativeIOS = Capacitor.isNativePlatform() && isIOS
  const { showError } = useMessage()
  const modalRef = useRef(null) as RefObject<HTMLIonModalElement>
  const [currentStep, setCurrentStep] = useState<StepKey>(StepKey.ITEM_DETAIL)
  const { routerNames } = useRouter<{ itemId: string }>()

  const STEPS = useMemo<Record<StepKey, JSX.Element>>(
    () => ({
      ITEM_DETAIL: (
        <Fade style={{ height: '100%' }}>
          <BuyNowItemDetail
            {...props}
            onClose={() => {
              if (modalRef.current?.canDismiss) {
                onClose()
              }
            }}
            onClickClose={onClose}
            onNextStep={setCurrentStep}
            onConfirmOrder={async () => {
              if (!modalRef.current) {
                return
              }
              modalRef.current.canDismiss = false
              await onPurchaseItem()
                .then(() => setCurrentStep(StepKey.COMPLETION))
                .catch(() => {})
              modalRef.current.canDismiss = true
            }}
            onSendVerificationSMS={async () => {
              await onSendVerificationSMS()
                .then(() => setCurrentStep(StepKey.SMS_VERIFICATION))
                .catch(showError)
            }}
          />
        </Fade>
      ),
      USER_INFO: (
        <Fade style={{ height: '100%' }}>
          <BuyNowUserInfo
            {...props}
            onClose={() => setCurrentStep(StepKey.ITEM_DETAIL)}
            onUserInfoSetting={inputs =>
              onUserInfoSetting(inputs).then(isPhoneNumberVerified => {
                if (isPhoneNumberVerified) {
                  setCurrentStep(StepKey.ITEM_DETAIL)
                }
              })
            }
            onSendVerificationSMS={async () =>
              await onSendVerificationSMS()
                .then(() => setCurrentStep(StepKey.SMS_VERIFICATION))
                .catch(showError)
            }
          />
        </Fade>
      ),
      DELIVERY_INFO: (
        <Fade style={{ height: '100%' }}>
          <BuyNowDeliveryInfo
            {...props}
            onClose={() => setCurrentStep(StepKey.ITEM_DETAIL)}
          />
        </Fade>
      ),
      PAYMENT_CREATE: (
        <Fade style={{ height: '100%' }}>
          <BuyNowPaymentCreate
            {...props}
            onCreatePayment={async values => {
              await onCreatePayment(values).then(() =>
                setCurrentStep(StepKey.ITEM_DETAIL)
              )
            }}
            onClose={() => setCurrentStep(StepKey.ITEM_DETAIL)}
          />
        </Fade>
      ),
      COMPLETION: (
        <Fade style={{ height: '100%' }}>
          <BuyNowCompletion
            onClose={onClose}
            itemId={props.item?.id}
            skuId={props.sku?.id}
          />
        </Fade>
      ),
      SMS_VERIFICATION: (
        <Fade style={{ height: '100%' }}>
          <BuyNowSMSVerification
            {...props}
            onVerificationPhoneNumber={async inputs => {
              await onVerificationPhoneNumber(inputs).then(() =>
                setCurrentStep(StepKey.ITEM_DETAIL)
              )
            }}
            onClose={() => setCurrentStep(StepKey.ITEM_DETAIL)}
          />
        </Fade>
      ),
    }),
    [
      onClose,
      onPurchaseItem,
      onCreatePayment,
      onUserInfoSetting,
      onSendVerificationSMS,
      onVerificationPhoneNumber,
      props,
      showError,
    ]
  )

  useEffect(() => {
    const ionicModal = modalRef.current
    return () => {
      ionicModal?.dismiss()
    }
  }, [])

  /*
   * To avoid the situation where when the user scrolls the modal,
   * the website content below the modal will be scrolled.
   * When BuyNowModal is open, overflowY of body have 'hidden' value
   * and it'll be reset when Buy Now Modal is close
   */
  useEffect(() => {
    if (!document) return
    if (open) {
      document.body.style.overflowY = 'hidden'
      return
    }
    if (isNativeIOS) {
      document.body.style.overflowY = 'unset'
      return
    }
    document.body.style.overflowY = 'auto'

    return () => {
      document.body.style.overflowY = 'auto'
    }
  }, [open])

  return (
    <Stack>
      <GlobalStyles
        styles={{
          'ion-modal': {
            '--height': '90%',
            position: 'fixed',
            bottom: 0,
          },
        }}
      />
      {/** ResultPlusのアフィリエイトスクリプトを設置 UT-15040 */}
      {config.applicationKey === 'utoniq' && (
        <ResultPlusScript
          title={routerNames.ArtistStoreItemDetail}
          isCompleted={currentStep === StepKey.COMPLETION}
        />
      )}
      <IonModal
        className="parent-modal"
        ref={modalRef}
        mode="ios"
        isOpen={open}
        onDidDismiss={() => {
          onClose()
          setCurrentStep(StepKey.ITEM_DETAIL)
        }}
        breakpoints={[0, 1]}
        initialBreakpoint={1}
        swipeToClose={false}
        handle={false}
      >
        <Stack
          sx={{
            height: '100%',
            overflowX: 'auto',
            backgroundColor: 'background.default',
            ...styleSets.hideScrollbar(),
          }}
        >
          {STEPS[currentStep]}
        </Stack>
      </IonModal>
    </Stack>
  )
}
